<script setup lang="ts">
import { computed, ref, watch } from 'vue';

const props = defineProps({
  modelValue: { type: [Object, Array, String, Number, Boolean, null] as unknown as any, default: () => ({}) },
  height: { type: String, default: '240px' },
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: unknown): void;
}>();

const text = ref('');
const errorText = ref<string | null>(null);
const skipNextWatch = ref(false);

function serialize(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

watch(
  () => props.modelValue,
  (v) => {
    if (skipNextWatch.value) { skipNextWatch.value = false; return; }
    text.value = serialize(v);
    errorText.value = null;
  },
  { immediate: true, deep: true },
);

const lineCount = computed(() => text.value.split('\n').length);

function onInput(ev: Event) {
  const t = (ev.target as HTMLTextAreaElement).value;
  text.value = t;
  if (!t.trim()) {
    errorText.value = null;
    skipNextWatch.value = true;
    emit('update:modelValue', null);
    return;
  }
  try {
    const parsed = JSON.parse(t);
    errorText.value = null;
    skipNextWatch.value = true;
    emit('update:modelValue', parsed);
  } catch (e: any) {
    errorText.value = e?.message || 'Некорректный JSON';
  }
}
</script>

<template>
  <div class="json-editor">
    <textarea
      class="json-editor__area"
      :style="{ height }"
      :readonly="readonly"
      :value="text"
      spellcheck="false"
      @input="onInput"
    />
    <div class="json-editor__meta">
      <span class="json-editor__lines">строк: {{ lineCount }}</span>
      <span v-if="errorText" class="json-editor__error">{{ errorText }}</span>
    </div>
  </div>
</template>

<style scoped>
.json-editor { display: flex; flex-direction: column; gap: 4px; width: 100%; }
.json-editor__area {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  font-size: 12px;
  line-height: 1.45;
  padding: 8px;
  border: 1px solid var(--dx-color-border, #ddd);
  border-radius: 4px;
  background: var(--dx-color-base-bg, #fafafa);
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
}
.json-editor__meta { display: flex; justify-content: space-between; font-size: 11px; color: #666; }
.json-editor__error { color: #c0392b; }
</style>
